// A PROJECT FOLDER AS A REGISTER (Files, project columns — turn 8, 8a/8b).
//
// The middle column of a project's Files lists documents, not files: a drawing
// is one row however many formats it is saved in (8614-05-0401_A.dwg and
// 8614-05-0401_A.pdf are one row, "05-0401 · Concrete details — slab · A"), and
// an older revision sits greyed and struck through under the current one,
// wherever it is filed (the folder itself or a Superseded subfolder).
//
//   docs    numbered to the house rule (doc-convention.js parseDocName)
//   fixes   in a folder Track checks, NOT numbered — the orange "rename" rows
//   others  everything else, grouped by the subfolder it sits in (Documents
//           Received, Working, …) — other people's files are not renamed
//
// Pure: plain data in, plain data out, tested in node.

import { parseDocName, revCompare, docState, isCheckedPath, isIssuable, CHECKED_FOLDERS } from './doc-convention.js';

const SUPERSEDED = /^superseded$/i;
const isSuperseded = (path) => (path || []).some((p) => SUPERSEDED.test(String(p).trim()));
const CAD_EXT = /\.(dwg|dxf)$/i;
const newest = (files) => files.reduce((m, f) => (String(f.modifiedTime || '') > m ? String(f.modifiedTime || '') : m), '');

/**
 * folder  { code, name, files: [{ id, name, path, modifiedTime, … }] } — a
 *         shapeProject folder (drive-folders.js), `path` = subfolders below it
 * project '8614'
 * sort    'number' | 'date'
 */
export function buildRegister(folder, project, { sort = 'number' } = {}) {
  const code = folder.code || null;
  const files = folder.files || [];
  const names = files.map((f) => f.name);
  const byNumber = new Map();     // number → Map rev → row
  const fixes = [];
  const others = new Map();       // heading → files

  for (const f of files) {
    const path = f.path || [];
    const p = parseDocName(f.name);
    const exempt = !isSuperseded(path) && code && CHECKED_FOLDERS.includes(code) && !isCheckedPath(code, path);
    if (p && (!project || p.project === String(project)) && !exempt) {
      if (!byNumber.has(p.number)) byNumber.set(p.number, new Map());
      const revs = byNumber.get(p.number);
      if (!revs.has(p.rev)) revs.set(p.rev, { key: `${p.number}_${p.rev}`, number: p.number, short: p.short, rev: p.rev, title: '', files: [], inSuperseded: false, path });
      const row = revs.get(p.rev);
      row.files.push(f);
      if (!row.title && p.title) row.title = p.title;
      if (isSuperseded(path)) row.inSuperseded = true;
      else row.path = path;
      continue;
    }
    if (code && CHECKED_FOLDERS.includes(code) && isCheckedPath(code, path) && isIssuable(f.name)) {
      const s = docState(f.name, { project, code, rel: path, siblings: names });
      fixes.push({ key: `fix_${f.id}`, file: f, files: [f], reason: (s && s.label) || 'Does not follow the numbering', suggestion: s && s.suggestion ? s.suggestion.name : '', path });
      continue;
    }
    const heading = path.length ? path.join(' / ') : '';
    if (!others.has(heading)) others.set(heading, []);
    others.get(heading).push(f);
  }

  const docs = [];
  for (const revs of byNumber.values()) {
    const order = [...revs.values()].sort((a, b) => revCompare(b.rev, a.rev));   // current first
    order.forEach((row, i) => {
      row.superseded = i > 0 || row.files.every((f) => isSuperseded(f.path));
      row.prevRev = order[i + 1] ? (order[i + 1].rev || 'first issue') : null;
      row.current = order[0].rev;
      row.modifiedTime = newest(row.files);
      row.formats = [...new Set(row.files.map((f) => (String(f.name).match(/\.([A-Za-z0-9]{1,5})$/) || [, ''])[1].toUpperCase()).filter(Boolean))];
      row.cad = row.files.find((f) => CAD_EXT.test(f.name)) || null;
      row.pdf = row.files.find((f) => /\.pdf$/i.test(f.name)) || null;
      docs.push(row);
    });
  }
  const numCmp = (a, b) => a.number.localeCompare(b.number, 'en-GB', { numeric: true }) || revCompare(b.rev, a.rev);
  if (sort === 'date') {
    docs.sort((a, b) => Number(a.superseded) - Number(b.superseded) || String(b.modifiedTime).localeCompare(String(a.modifiedTime)) || numCmp(a, b));
  } else docs.sort(numCmp);
  fixes.sort((a, b) => (sort === 'date' ? String(b.file.modifiedTime || '').localeCompare(String(a.file.modifiedTime || '')) : 0)
    || String(a.file.name).localeCompare(String(b.file.name), 'en-GB', { numeric: true }));
  const groups = [...others.entries()]
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b, 'en-GB', { numeric: true })))
    .map(([heading, list]) => ({ heading, files: list.slice().sort((a, b) => (sort === 'date'
      ? String(b.modifiedTime || '').localeCompare(String(a.modifiedTime || ''))
      : String(a.name).localeCompare(String(b.name), 'en-GB', { numeric: true }))) }));
  return { docs, fixes, others: groups };
}

/** The desktop program a file opens in, by its extension — null for none. */
export function desktopApp(name) {
  const ext = (String(name || '').match(/\.([A-Za-z0-9]{1,5})$/) || [, ''])[1].toLowerCase();
  return DESKTOP[ext] || null;
}
const DESKTOP = {
  dwg: 'AutoCAD', dxf: 'AutoCAD', dwt: 'AutoCAD', dws: 'AutoCAD',
  rvt: 'Revit', rfa: 'Revit', ted: 'Tedds', tedds: 'Tedds',
  xlsx: 'Excel', xls: 'Excel', csv: 'Excel', docx: 'Word', doc: 'Word', pptx: 'PowerPoint',
  skp: 'SketchUp', nwd: 'Navisworks', nwc: 'Navisworks',
};
